/**
 * Pure helpers for the "Add Teachers" entry screen of the Teacher Allocation step.
 *
 * Three sources feed the same review list: names typed/pasted into the multiline field,
 * structured spreadsheets parsed in the browser, and documents/images sent to the
 * server-side extraction endpoint. Everything here is side-effect free so it can be unit
 * tested; the React component owns file reading, dispatching, and state.
 */
import {
  DEFAULT_DATA_UPLOAD_CONFIG,
  type DataUploadCounts,
  type DataUploadSkippedRow,
  normalizeDataUploadCounts,
} from 'common/helpers/dataUpload'

import {nameRuleViolation, notARecordRows, skippedRow} from './recordRules'

// --- Upload limits & accepted formats -------------------------------------------------

/**
 * Upload guardrails shown to the user and enforced before any file is read or sent.
 *
 * `maxFileBytes` is only the default: the entry screen uses the value read from
 * `GET data-upload/use-cases` (files travel in pieces, so no proxy limit applies).
 */
export const TEACHER_UPLOAD_LIMITS = {
  /** One file at a time; adding another replaces it. Raise this to allow a multi-file queue. */
  maxFiles: 1,
  maxFileBytes: DEFAULT_DATA_UPLOAD_CONFIG.maxFileBytes,
} as const

/** What a file is; decides its icon, and (without the server) whether the browser reads it. */
export type TeacherFileKind = 'spreadsheet' | 'document' | 'image'

/**
 * Server-side extraction (`POST onboarding/extract-teachers`: Word, PDF, photos and scans, and
 * sheets with no recognisable header). The endpoint only exists on the AI servers, so the entry
 * screen passes `false` to the helpers below where it is unavailable and the upload stays
 * Excel-only there.
 */
export const TEACHER_UPLOAD_EXTRACTION_ENABLED = true

/** Accepted without extraction: the one format the browser parses deterministically. */
const TEACHER_FILE_KINDS_EXCEL_ONLY: Readonly<Record<string, TeacherFileKind>> = {
  xlsx: 'spreadsheet',
}

/**
 * Extension -> processing kind with extraction available. Every kind is uploaded and read by
 * the server there (a sheet with headers without the model); `spreadsheet` only matters for
 * the local mock, which parses it in the browser first. Legacy `.xls`
 * and `.doc` are refused by the backend (FILE_TYPE_NOT_ALLOWED), so they are not listed:
 * the user is asked to re-save as `.xlsx` / `.docx`. Executables and anything else are rejected.
 */
const TEACHER_FILE_KINDS_EXTRACTION: Readonly<Record<string, TeacherFileKind>> = {
  xlsx: 'spreadsheet',
  csv: 'spreadsheet',
  ods: 'document',
  txt: 'document',
  docx: 'document',
  odt: 'document',
  pdf: 'document',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  webp: 'image',
  heic: 'image',
  heif: 'image',
}

const teacherFileKinds = (extractionEnabled: boolean) =>
  extractionEnabled ? TEACHER_FILE_KINDS_EXTRACTION : TEACHER_FILE_KINDS_EXCEL_ONLY

/** MIME types that may accompany the extensions above. Used for a belt-and-braces check. */
const TEACHER_FILE_MIME_PREFIXES = [
  'text/csv',
  'text/plain',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'image/',
  'application/octet-stream',
  '',
]

/** `accept` attribute value for the hidden file input. */
export const teacherUploadAccept = (
  extractionEnabled: boolean = TEACHER_UPLOAD_EXTRACTION_ENABLED
): string =>
  Object.keys(teacherFileKinds(extractionEnabled))
    .map((ext) => `.${ext}`)
    .join(',')

/** `accept` value with extraction at its default. Prefer `teacherUploadAccept(enabled)`. */
export const TEACHER_UPLOAD_ACCEPT = teacherUploadAccept()

/** Lower-cased extension of a file name, without the dot ("" when there is none). */
export const fileExtension = (name: string): string => {
  const idx = name.lastIndexOf('.')
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : ''
}

/** Processing kind for a file, or null when its type is not accepted. */
export const classifyTeacherFile = (
  file: Pick<File, 'name' | 'type'>,
  extractionEnabled: boolean = TEACHER_UPLOAD_EXTRACTION_ENABLED
): TeacherFileKind | null => {
  const kind = teacherFileKinds(extractionEnabled)[fileExtension(file.name)]
  if (!kind) return null
  const mime = (file.type || '').toLowerCase()
  const mimeOk = TEACHER_FILE_MIME_PREFIXES.some((p) =>
    p === '' ? mime === '' : mime.startsWith(p)
  )
  return mimeOk ? kind : null
}

export type TeacherFileRejection = 'unsupported' | 'too-large' | 'too-many' | 'duplicate'

type FileLike = Pick<File, 'name' | 'type' | 'size'>

/**
 * Validate files being added to the upload list against `TEACHER_UPLOAD_LIMITS`.
 * Files are checked in order, so once the count or byte budget is exhausted every
 * later file is rejected rather than silently dropped.
 */
export const validateTeacherFiles = <T extends FileLike>(
  existing: readonly T[],
  incoming: readonly T[],
  options: {extractionEnabled?: boolean; maxFileBytes?: number} = {}
): {accepted: T[]; rejected: Array<{file: T; reason: TeacherFileRejection}>} => {
  const extractionEnabled = options.extractionEnabled ?? TEACHER_UPLOAD_EXTRACTION_ENABLED
  const maxFileBytes = options.maxFileBytes ?? TEACHER_UPLOAD_LIMITS.maxFileBytes
  const accepted: T[] = []
  const rejected: Array<{file: T; reason: TeacherFileRejection}> = []
  const seen = new Set(existing.map((f) => `${f.name}::${f.size}`))
  let count = existing.length

  for (const file of incoming) {
    const key = `${file.name}::${file.size}`
    if (!classifyTeacherFile(file, extractionEnabled)) {
      rejected.push({file, reason: 'unsupported'})
      continue
    }
    if (file.size > maxFileBytes) {
      rejected.push({file, reason: 'too-large'})
      continue
    }
    if (seen.has(key)) {
      rejected.push({file, reason: 'duplicate'})
      continue
    }
    if (count >= TEACHER_UPLOAD_LIMITS.maxFiles) {
      rejected.push({file, reason: 'too-many'})
      continue
    }
    seen.add(key)
    count += 1
    accepted.push(file)
  }

  return {accepted, rejected}
}

/** "10 MB" style label for the limits copy. */
export const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    const mb = bytes / (1024 * 1024)
    return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`
  }
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}

// --- Typed / pasted names -------------------------------------------------------------

/** Leading list markers pasted from Word/Docs ("1.", "2)", "•", "-", "*") — safe to strip. */
const LIST_MARKER_RE = /^(?:\(?\d{1,3}[.)\]:]|[-•*·–—▪◦])\s+/

/**
 * Split free text into teacher names. Separators are new lines, tabs, commas and
 * semicolons — never plain spaces, because full names contain spaces. Nothing else is
 * guessed: a line the user typed is a name, exactly as typed (trimmed, markers removed).
 */
export const splitTeacherNames = (text: string): string[] =>
  text
    .split(/[\r\n\t,;]+/)
    .map((part) => part.trim().replace(LIST_MARKER_RE, '').trim())
    .filter((part) => part.length > 0)

/**
 * Normalize clipboard text into one name per line so the numbered field shows the list
 * the way it will be reviewed. Returns null when the text is already a single plain name
 * (so the default paste behaviour is kept and the caret lands where the user expects).
 */
export const normalizePastedNames = (text: string): string | null => {
  const names = splitTeacherNames(text)
  if (names.length === 0) return null
  if (names.length === 1 && names[0] === text.trim()) return null
  return names.join('\n')
}

// --- Review-list rows -----------------------------------------------------------------

/** Subject/class/section relationship found next to a teacher in an uploaded file. */
export interface TeacherAllocationHint {
  subject?: string
  class?: string
  section?: string
}

/**
 * One row of the review list — the shape `manualData.teachers` stores. Underscore fields
 * are review-only markers (like the existing `_nameInput`) and never reach the API.
 */
export interface TeacherEntryRow {
  first_name: string
  last_name: string
  employeeId: string
  level: string
  email: string
  cnic: string
  /** Relationships preserved from the source so Teacher Allocation can be pre-filled. */
  allocationHints?: TeacherAllocationHint[]
  /** Set on low-confidence or incomplete extraction results; cleared when the user edits the name. */
  _needsReview?: boolean
  /** Name of the uploaded file the row came from, for the review badge tooltip. */
  _sourceFile?: string
}

/** Split a full name the way the review table does (first token + rest). */
export const splitFullName = (fullName: string): {first: string; last: string} => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  return {first: parts[0] || '', last: parts.slice(1).join(' ') || ''}
}

export const makeTeacherRow = (
  fullName: string,
  extra: Partial<Omit<TeacherEntryRow, 'first_name' | 'last_name'>> = {}
): TeacherEntryRow => {
  const {first, last} = splitFullName(fullName)
  return {
    first_name: first,
    last_name: last,
    employeeId: '',
    level: '',
    email: '',
    cnic: '',
    ...extra,
  }
}

/** Rows for names typed/pasted into the entry field. */
export const namesToTeacherRows = (names: readonly string[]): TeacherEntryRow[] =>
  names.map((name) => makeTeacherRow(name))

const rowFullName = (row: {first_name?: string; last_name?: string; _nameInput?: string}) =>
  (row._nameInput !== undefined
    ? row._nameInput
    : `${row.first_name || ''} ${row.last_name || ''}`.trim()
  ).trim()

/**
 * Collapse rows that are certainly the same person: same name (case-insensitive) AND the
 * same non-empty Employee ID. Same-name rows without an ID are kept — the review table
 * flags them and asks for an Employee ID, which is the only way to tell them apart.
 */
export const dedupeExactTeacherRows = <
  T extends {first_name?: string; last_name?: string; employeeId?: string},
>(
  rows: readonly T[]
): T[] => {
  const seen = new Set<string>()
  const out: T[] = []
  for (const row of rows) {
    const id = String(row.employeeId ?? '').trim()
    if (!id) {
      out.push(row)
      continue
    }
    const key = `${rowFullName(row).toLowerCase()}::${id.toLowerCase()}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(row)
  }
  return out
}

// --- Spreadsheets (deterministic, no AI) ----------------------------------------------

/** U+FEFF byte-order mark that Excel prepends to UTF-8 CSV exports. */
const BOM = String.fromCharCode(0xfeff)
const BOM_RE = new RegExp('^' + BOM, 'g')
const stripBom = (text: string) => (text.startsWith(BOM) ? text.slice(1) : text)

/** Minimal RFC-4180 CSV reader (quotes, escaped quotes, CRLF). Trailing blank rows are dropped. */
export function parseCsvRows(csvText: string): string[][] {
  const text = stripBom(csvText)
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let i = 0
  let inQuotes = false

  const flushRow = () => {
    row.push(cell)
    rows.push(row)
    row = []
    cell = ''
  }

  while (i < text.length) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"'
          i += 2
          continue
        }
        inQuotes = false
        i++
        continue
      }
      cell += c
      i++
      continue
    }
    if (c === '"') {
      inQuotes = true
      i++
      continue
    }
    if (c === ',') {
      row.push(cell)
      cell = ''
      i++
      continue
    }
    if (c === '\r') {
      i++
      if (text[i] === '\n') i++
      flushRow()
      continue
    }
    if (c === '\n') {
      i++
      flushRow()
      continue
    }
    cell += c
    i++
  }
  flushRow()
  while (rows.length && rows[rows.length - 1].every((x) => !x.trim())) {
    rows.pop()
  }
  return rows
}

export function normalizeCsvHeader(h: string) {
  return h.replace(BOM_RE, '').trim().toLowerCase().replace(/\s+/g, ' ')
}

export type TeacherRowsParseError = 'NO_DATA' | 'NO_NAME_COLUMN'

/** Header cell -> lookup key: lower-cased, whitespace-collapsed, "(Optional)"-style suffix dropped. */
const headerKey = (h: string) =>
  normalizeCsvHeader(h)
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/[*:]+$/, '')
    .trim()

const NAME_HEADERS = ['full name', 'teacher name', 'teacher', 'name of teacher']
const FIRST_HEADERS = ['first name']
const LAST_HEADERS = ['last name']
const FALLBACK_NAME_HEADERS = ['name']

/** How far down a sheet we look for the header row (instruction blocks, titles, blank rows). */
const HEADER_SCAN_ROWS = 30

/** Index of the first row that carries a recognisable name column, or -1. */
const findHeaderRow = (rows: string[][]): number => {
  const limit = Math.min(rows.length, HEADER_SCAN_ROWS)
  for (let r = 0; r < limit; r++) {
    const keys = rows[r].map(headerKey)
    const has = (cands: string[]) => cands.some((c) => keys.includes(c))
    if (has(NAME_HEADERS) || has(FIRST_HEADERS) || has(LAST_HEADERS)) return r
  }
  for (let r = 0; r < limit; r++) {
    if (rows[r].map(headerKey).includes(FALLBACK_NAME_HEADERS[0])) return r
  }
  return -1
}

/**
 * Parse a structured teacher sheet (the sample template or any sheet with a recognisable
 * name column). Optional Employee ID and Subject / Class / Section columns are picked up
 * when present; the relationships are kept on the row as `allocationHints`.
 *
 * The header row is located by content, not position, so a title or instruction block above
 * the table (as in the sample template) is skipped rather than imported as teachers.
 *
 * Returns `error` when the sheet cannot be trusted (no header/name column) so the caller
 * can fall back to extraction instead of guessing.
 *
 * Rows the server would refuse are left out and listed in `skipped` with the server's code
 * (see recordRules.ts): a name with no letters or with digits, one not in English letters, and
 * headings or notes inside the table.
 */
export function parseTeachersRows(
  rows: string[][],
  sourceFile?: string
): {rows: TeacherEntryRow[]; skipped: DataUploadSkippedRow[]; error?: TeacherRowsParseError} {
  if (rows.length < 2) {
    return {rows: [], skipped: [], error: 'NO_DATA'}
  }

  const headerRow = findHeaderRow(rows)
  if (headerRow < 0) {
    return {rows: [], skipped: [], error: 'NO_NAME_COLUMN'}
  }

  const headers = rows[headerRow].map(headerKey)
  const col = (candidates: string[]) => {
    for (const c of candidates) {
      const idx = headers.indexOf(c)
      if (idx >= 0) return idx
    }
    return -1
  }

  let idxFull = col(NAME_HEADERS)
  const idxFirst = col(FIRST_HEADERS)
  const idxLast = col(LAST_HEADERS)
  let idxEmp = col(['employee id', 'employee_id', 'employeeid', 'emp id', 'emp_id'])
  const idxSubject = col(['subject', 'subjects', 'subject name'])
  const idxClass = col(['class', 'classes', 'class name', 'grade'])
  const idxSection = col(['section', 'sections'])

  if (idxFull < 0 && idxFirst < 0 && idxLast < 0) {
    idxFull = col(FALLBACK_NAME_HEADERS)
  }

  if (idxEmp < 0) {
    const staff = col(['staff id', 'staff_id', 'staffid'])
    if (staff >= 0) idxEmp = staff
  }

  if (idxFull < 0 && idxFirst < 0 && idxLast < 0) {
    return {rows: [], skipped: [], error: 'NO_NAME_COLUMN'}
  }

  const dataRows = rows.slice(headerRow + 1).map((row) => row.map((c) => (c ?? '').toString()))
  const notes = notARecordRows(
    dataRows,
    [idxFull, idxFirst, idxLast].filter((idx) => idx >= 0)
  )

  const out: TeacherEntryRow[] = []
  const skipped: DataUploadSkippedRow[] = []
  for (let r = headerRow + 1; r < rows.length; r++) {
    const cells = rows[r].map((c) => (c ?? '').toString().trim())
    if (!cells.some((x) => x.length > 0)) continue

    let first = ''
    let last = ''
    if (idxFull >= 0) {
      const split = splitFullName(cells[idxFull] || '')
      first = split.first
      last = split.last
    } else {
      first = idxFirst >= 0 ? cells[idxFirst] || '' : ''
      last = idxLast >= 0 ? cells[idxLast] || '' : ''
    }

    const full = `${first} ${last}`.trim()
    if (!full) continue
    // Same order as the server: a name with no letters ("50") first, then a heading or note.
    const violation = nameRuleViolation(full)
    const code =
      violation === 'NOT_A_NAME' || !notes.has(r - headerRow - 1) ? violation : 'NOT_A_RECORD'
    if (code) {
      skipped.push(skippedRow(full, code))
      continue
    }

    const employeeId = idxEmp >= 0 ? cells[idxEmp] || '' : ''
    const hint: TeacherAllocationHint = {}
    if (idxSubject >= 0 && cells[idxSubject]) hint.subject = cells[idxSubject]
    if (idxClass >= 0 && cells[idxClass]) hint.class = cells[idxClass]
    if (idxSection >= 0 && cells[idxSection]) hint.section = cells[idxSection]
    const hasHint = Object.keys(hint).length > 0

    const row: TeacherEntryRow = {
      first_name: first,
      last_name: last,
      employeeId,
      level: '',
      email: '',
      cnic: '',
    }
    if (hasHint) row.allocationHints = [hint]
    if (sourceFile) row._sourceFile = sourceFile
    out.push(row)
  }

  return {rows: out, skipped}
}

// --- Server-side extraction (documents, images, unstructured sheets) ------------------

/** One teacher as returned by `POST onboarding/extract-teachers` (see docs). */
export interface ExtractedTeacher {
  name?: string
  employeeId?: string | null
  subject?: string | null
  class?: string | null
  section?: string | null
  /** 0..1; below `EXTRACTION_REVIEW_THRESHOLD` the row is flagged for review. */
  confidence?: number | null
}

export const EXTRACTION_REVIEW_THRESHOLD = 0.8

/** What one file's extraction said, success or failure. */
export interface TeacherExtraction {
  rows: TeacherEntryRow[]
  /** The file's `reason` (one of the data-upload codes, upper-case) when it failed. */
  error?: string
  /** The server's plain summary: "Read 52 rows. 50 teachers are ready. 2 rows were left out…". */
  message: string | null
  /** The numbers behind `message`, for a translated summary. */
  counts: DataUploadCounts | null
  /** Rows left out, with their code (`NOT_A_NAME`, `NOT_A_RECORD`, …). */
  skipped: DataUploadSkippedRow[]
}

/**
 * Normalize the extraction response into review rows. Tolerates the API envelope
 * (`{result: {files: [...]}}`), the bare per-file body (`{files: [{teachers}]}`) and a flat
 * `{teachers}` body, and never trusts a
 * row blindly: anything without a name is dropped, anything below the confidence
 * threshold (or with no confidence at all) is flagged `_needsReview`. The file's `message`,
 * `counts` and `report.skipped` come along, on success and on failure alike.
 */
export const normalizeExtractionResponse = (res: any, sourceFile: string): TeacherExtraction => {
  // node-api wraps every body in `{statusCode, message, status: 'true', result}`. The payload
  // is `result`; reading the envelope itself mistook its `status: 'true'` for a failed file.
  const data = res?.result ?? res?.data ?? res
  const fileResult = Array.isArray(data?.files) ? data.files[0] : data
  if (!fileResult) return {rows: [], error: 'EMPTY', message: null, counts: null, skipped: []}
  const message =
    typeof fileResult.message === 'string' && fileResult.message.trim()
      ? fileResult.message.trim()
      : typeof fileResult.detail === 'string' && fileResult.detail.trim()
        ? fileResult.detail.trim()
        : null
  const details = {
    message,
    counts: normalizeDataUploadCounts(fileResult.counts),
    skipped: Array.isArray(fileResult.report?.skipped)
      ? (fileResult.report.skipped as DataUploadSkippedRow[])
      : [],
  }
  if (fileResult.status && fileResult.status !== 'ok') {
    return {rows: [], error: String(fileResult.reason || fileResult.status), ...details}
  }
  const teachers: ExtractedTeacher[] = Array.isArray(fileResult.teachers) ? fileResult.teachers : []

  const rows: TeacherEntryRow[] = []
  for (const t of teachers) {
    const name = String(t?.name ?? '').trim()
    if (!name) continue
    const confidence = typeof t.confidence === 'number' ? t.confidence : null
    const hint: TeacherAllocationHint = {}
    if (t.subject) hint.subject = String(t.subject).trim()
    if (t.class) hint.class = String(t.class).trim()
    if (t.section) hint.section = String(t.section).trim()
    const row = makeTeacherRow(name, {
      employeeId: String(t.employeeId ?? '').trim(),
      _sourceFile: sourceFile,
    })
    if (Object.keys(hint).length > 0) row.allocationHints = [hint]
    if (confidence === null || confidence < EXTRACTION_REVIEW_THRESHOLD) row._needsReview = true
    rows.push(row)
  }
  return {rows, ...details}
}
